/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 리사이저 클래스를 정의한다.
 *
 * @file /src/utils/Resizer.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 6. 14.
 */
import * as parent from '@base/Base';
import { Viewport } from '@base/Viewport';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { Dragger } from '@utils/Dragger';
import { Scroller } from './Scroller';

export interface Listeners<T> extends parent.Listeners<T> {
    /**
     * 객체가 리사이즈중일 때
     */
    resize?: ($target: Dom, rect: DOMRect, position: { x: number; y: number }, $guideline: Dom) => void;

    /**
     * 객체 리사이즈가 시작될 때
     */
    start?: ($target: Dom, rect: DOMRect, position: { x: number; y: number }, $guideline: Dom) => void;

    /**
     * 객체 리사이즈가 완료되었을 때
     */
    end?: ($target: Dom, rect: DOMRect, position: { x: number; y: number }) => void;

    /**
     * 리사이저에 마우스포인트가 들어왔을 때
     */
    mouseenter?: ($target: Dom) => void;

    /**
     * 리사이저에 마우스포인트가 나갔을 때
     */
    mouseleave?: ($target: Dom) => void;

    /**
     * 리사이저를 더블클릭했을 때
     */
    dblclick?: ($target: Dom) => void;
}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 리사이즈 방향
     */
    directions: [boolean, boolean, boolean, boolean];

    /**
     * 가이드라인 표시여부
     */
    guidelines?: [boolean, boolean, boolean, boolean];

    /**
     * 최소 너비
     */
    minWidth?: number;

    /**
     * 최대 너비
     */
    maxWidth?: number;

    /**
     * 최소 높이
     */
    minHeight?: number;

    /**
     * 최대 높이
     */
    maxHeight?: number;

    /**
     * 리사이즈에 따라 스크롤러가 움직일 수 있는 경우 스크롤 이벤트를 추적하기 위한 스크롤러
     */
    scroller?: Scroller;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Resizer extends parent.Base {
    /**
     * 뷰포트
     */
    static viewport: Viewport;

    /**
     * 리사이즈 대상 DOM
     */
    $target: Dom;

    /**
     * 리사이즈 대상의 부모 DOM
     */
    $parent: Dom;

    /**
     * 리사이즈 핸들 DOM 목록
     */
    $resizers: Dom[];

    /**
     * 리사이즈 방향 활성화 여부
     */
    directions: {
        top: boolean;
        right: boolean;
        bottom: boolean;
        left: boolean;
        topLeft: boolean;
        topRight: boolean;
        bottomLeft: boolean;
        bottomRight: boolean;
    };

    /**
     * 방향별 가이드라인 표시 여부
     */
    guidelines: {
        top: boolean;
        right: boolean;
        bottom: boolean;
        left: boolean;
    };

    /**
     * 최소 너비 (px)
     */
    minWidth: number = 0;

    /**
     * 최대 너비 (px, 0이면 제한 없음)
     */
    maxWidth: number = 0;

    /**
     * 최소 높이 (px)
     */
    minHeight: number = 0;

    /**
     * 최대 높이 (px, 0이면 제한 없음)
     */
    maxHeight: number = 0;

    /**
     * 리사이즈에 따라 스크롤러가 움직일 수 있는 경우 스크롤 이벤트를 추적하기 위한 스크롤러
     */
    scroller: Scroller;

    /**
     * 스크롤러 active 이벤트 핸들러 (addEvent/removeEvent 에서 동일 참조를 사용하기 위해 저장)
     */
    private readonly onScrollHandler: (me: Scroller, direction: string) => void;

    /**
     * 리사이저 개별 객체를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(
        $target: Dom,
        $parent: Dom,
        public properties: Properties<Resizer> = null
    ) {
        super(properties);
        this.$target = $target;
        this.$parent = $parent;
        if (!(this.properties.directions instanceof Array) || this.properties.directions.length != 4) {
            this.properties.directions = [true, true, true, true];
        }
        if (!(this.properties.guidelines instanceof Array) || this.properties.guidelines.length != 4) {
            this.properties.guidelines = [true, true, true, true];
        }
        this.minWidth = this.properties.minWidth ?? 0;
        this.maxWidth = this.properties.maxWidth ?? 0;
        this.minHeight = this.properties.minHeight ?? 0;
        this.maxHeight = this.properties.maxHeight ?? 0;
        this.scroller = this.properties.scroller ?? null;
        this.onScrollHandler = () => this.onScroll();

        this.$resizers = [];
        Html.get('div[data-role=resizer]', $target)?.remove();

        this.directions = {
            top: this.properties.directions[0],
            right: this.properties.directions[1],
            bottom: this.properties.directions[2],
            left: this.properties.directions[3],
            topRight: this.properties.directions[0] && this.properties.directions[1],
            topLeft: this.properties.directions[0] && this.properties.directions[3],
            bottomRight: this.properties.directions[2] && this.properties.directions[1],
            bottomLeft: this.properties.directions[2] && this.properties.directions[3],
        };

        this.guidelines = {
            top: this.properties.guidelines[0],
            right: this.properties.guidelines[1],
            bottom: this.properties.guidelines[2],
            left: this.properties.guidelines[3],
        };

        for (const direction in this.directions) {
            if (this.directions[direction] == true) {
                this.$resizers.push(this.$getResizer(direction));
            }
        }

        $target.append(this.$resizers);
    }

    /**
     * 뷰포트를 지정한다.
     *
     * @param viewport - 뷰포트
     */
    static setViewport(viewport: Viewport): void {
        Resizer.viewport = viewport;
    }

    /**
     * 크기조절 DOM을 가져온다.
     *
     * @param direction - 방향
     * @returns 크기조절 DOM
     */
    $getResizer(direction: string): Dom {
        const $resizer = Html.create('div', {
            'data-role': 'resizer',
            'data-direction': direction,
        });
        $resizer.on('mouseenter', (_, e: MouseEvent) => {
            this.fireEvent('mouseenter', [Html.el(e.currentTarget)]);
        });
        $resizer.on('mouseleave', (_, e: MouseEvent) => {
            if (this.isActive() == false) {
                this.fireEvent('mouseleave', [Html.el(e.currentTarget)]);
            }
        });
        $resizer.on('dblclick', (_, e: MouseEvent) => {
            this.fireEvent('dblclick', [Html.el(e.currentTarget)]);
        });

        const cursors = {
            left: 'ew-resize',
            right: 'ew-resize',
            top: 'ns-resize',
            bottom: 'ns-resize',
            topLeft: 'nwse-resize',
            topRight: 'nesw-resize',
            bottomLeft: 'nesw-resize',
            bottomRight: 'nwse-resize',
        };

        new Dragger($resizer, {
            pointerType: ['mouse'],
            cursor: cursors[direction],
            delay: 200,
            listeners: {
                start: ($resizer: Dom, tracker: Dragger) => this.handleDragStart($resizer, tracker),
                drag: ($resizer: Dom, tracker: Dragger) => this.handleDrag($resizer, tracker),
                end: ($resizer: Dom, tracker: Dragger) => this.handleDragEnd($resizer, tracker),
            },
        });

        return $resizer;
    }

    /**
     * 드래그 시작 시 호출되는 핸들러
     *
     * @param $resizer - 리사이즈 핸들 DOM
     * @param tracker - 드래거
     */
    private handleDragStart($resizer: Dom, tracker: Dragger): void {
        if ($resizer.getAttr('disabled') === 'true') return;

        this.$target.addClass('noselect');
        const direction = $resizer.getData('direction');
        const rect = this.getResizeRect(direction, { x: null, y: null });
        this.resetGuideline();
        const $guide = this.setGuideline(rect);
        this.$parent.append($guide);

        if (this.$target.is('dialog') == true) $guide.getEl().showPopover();

        this.scroller?.addEvent('active', this.onScrollHandler);
        this.fireEvent('mouseenter', [$resizer]);
        this.fireEvent('start', [this.$target, rect, tracker.getStart(), $guide]);
    }

    /**
     * 드래그 중일 때 호출되는 핸들러
     *
     * @param $resizer - 리사이즈 핸들 DOM
     * @param tracker - 드래거
     */
    private handleDrag($resizer: Dom, tracker: Dragger): void {
        if ($resizer.getAttr('disabled') === 'true') return;

        const direction = $resizer.getData('direction');
        const rect = this.getResizeRect(direction, tracker.getPosition());
        const $guide = this.setGuideline(rect);

        this.fireEvent('resize', [this.$target, rect, tracker.getPosition(), $guide]);
    }

    /**
     * 드래그 종료 시 호출되는 핸들러
     *
     * @param $resizer - 리사이즈 핸들 DOM
     * @param tracker - 드래거
     */
    private handleDragEnd($resizer: Dom, tracker: Dragger): void {
        if ($resizer.getAttr('disabled') === 'true') return;

        this.$target.removeClass('noselect');
        const direction = $resizer.getData('direction');
        const rect = this.getResizeRect(direction, tracker.getPosition());

        Html.get('> div[data-role="resize-guide"]', this.$parent)?.remove();

        this.scroller?.removeEvent('active', this.onScrollHandler);
        this.fireEvent('mouseleave', [$resizer]);
        this.fireEvent('end', [this.$target, rect, tracker.getPosition()]);
    }

    /**
     * 리사이즈를 비활성화한다.
     *
     * @param disabled - 비활성화 여부
     */
    setDisable(disabled: boolean): void {
        for (const $resizer of this.$resizers) {
            $resizer.setAttr('disabled', `${disabled}`);
        }
    }

    /**
     * 최대너비를 설정한다.
     *
     * @param maxWidth - 최대너비
     */
    setMaxWidth(maxWidth: number): void {
        this.maxWidth = maxWidth ?? 0;
    }

    /**
     * 최대높이를 설정한다.
     *
     * @param maxHeight - 최대높이
     */
    setMaxHeight(maxHeight: number): void {
        this.maxHeight = maxHeight ?? 0;
    }

    /**
     * 최소너비를 설정한다.
     *
     * @param minWidth - 최소너비
     */
    setMinWidth(minWidth: number): void {
        this.minWidth = minWidth ?? 0;
    }

    /**
     * 최소높이를 설정한다.
     *
     * @param minHeight - 최소높이
     */
    setMinHeight(minHeight: number): void {
        this.minHeight = minHeight ?? 0;
    }

    /**
     * 리사이즈되는 DOM 의 Rect 를 가져온다.
     *
     * @param direction - 리사이즈 방향
     * @param position - 마우스 포인트 위치
     * @return 리사이즈되는 Rect
     */
    getResizeRect(direction: string, position: { x: number; y: number }): DOMRect {
        const rect = this.$target.getEl().getBoundingClientRect();
        const guide = new DOMRect(rect.x, rect.y, rect.width, rect.height);

        let parentOffset = { left: 0, top: 0 };
        let parentScroll = { left: 0, top: 0 };
        if (this.$target.getStyle('position') !== 'fixed' && this.$target.getStyle('position') !== 'absolute') {
            parentOffset = this.$parent.getOffset();
            parentScroll = this.$parent.getScroll();
            guide.x = rect.x - parentOffset.left + parentScroll.left;
            guide.y = rect.y - parentOffset.top + parentScroll.top;
        }

        if (direction.indexOf('top') === 0) {
            if (position.y !== null) {
                guide.height = Math.max(this.minHeight, rect.height + (rect.y - position.y));
                if (this.maxHeight > 0 && guide.height > this.maxHeight) {
                    guide.height = this.maxHeight;
                }
                guide.y = rect.y - parentOffset.top + parentScroll.top + (rect.height - guide.height);

                if (Resizer.viewport.taskbar !== null) {
                    const taskbarHeight = Resizer.viewport.taskbar.$getComponent().getHeight();
                    guide.y = guide.y < taskbarHeight ? taskbarHeight : guide.y;
                }
            }
        } else if (direction.indexOf('bottom') === 0) {
            if (position.y !== null) {
                guide.height = Math.max(this.minHeight, position.y - rect.y);
                if (this.maxHeight > 0 && guide.height > this.maxHeight) {
                    guide.height = this.maxHeight;
                }
            }
        }

        switch (direction) {
            case 'left':
            case 'topLeft':
            case 'bottomLeft':
                if (position.x !== null) {
                    guide.width = Math.max(this.minWidth, rect.width + (rect.x - position.x));
                    if (this.maxWidth > 0 && guide.width > this.maxWidth) {
                        guide.width = this.maxWidth;
                    }
                    guide.x = rect.x - parentOffset.left + parentScroll.left + (rect.width - guide.width);
                }
                break;

            case 'right':
            case 'topRight':
            case 'bottomRight':
                if (position.x !== null) {
                    guide.width = Math.max(this.minWidth, position.x - rect.x);
                    if (this.maxWidth > 0 && guide.width > this.maxWidth) {
                        guide.width = this.maxWidth;
                    }
                }
                break;
        }

        return guide;
    }

    /**
     * 모든 가이드라인을 그린다.
     *
     * @param rect - 가이드라인 기준 Rect
     */
    setGuideline(rect: DOMRect): Dom {
        const $guide =
            Html.get('> div[data-role="resize-guide"]', this.$parent) != null
                ? Html.get('> div[data-role="resize-guide"]', this.$parent)
                : Html.create('div', { 'data-role': 'resize-guide' });

        if (this.$target.is('dialog') == true) $guide.setAttr('popover', 'manual');

        $guide.setStyle('top', rect.y + 'px');
        $guide.setStyle('left', rect.x + 'px');

        $guide.setStyle('width', rect.width + 'px');
        $guide.setStyle('height', rect.height + 'px');

        if (this.guidelines.top == false) {
            $guide.setStyle('border-top', '0');
        }

        if (this.guidelines.right == false) {
            $guide.setStyle('border-right', '0');
        }

        if (this.guidelines.bottom == false) {
            $guide.setStyle('border-bottom', '0');
        }

        if (this.guidelines.left == false) {
            $guide.setStyle('border-left', '0');
        }

        return $guide;
    }

    /**
     * 모든 가이드라인을 초기화한다.
     */
    resetGuideline(): void {
        Html.get('div[data-role="resize-guide"]')?.remove();
    }

    /**
     * 스크롤러가 스크롤될 때 이벤트를 처리한다.
     */
    onScroll(): void {
        const tracker = Dragger.getActivePointer();
        if (tracker === null) return;
        this.handleDrag(tracker.getTarget(), tracker);
    }

    /**
     * 리사이즈가 활성화된 상태인지 가져온다.
     *
     * @return 활성화 여부
     */
    isActive(): boolean {
        return Dragger.getActivePointer()?.getTarget()?.is('div[data-role=resizer]') == true;
    }

    /**
     * 객체를 제거한다.
     */
    destroy(): void {
        this.scroller?.removeEvent('active', this.onScrollHandler);
        this.$target = null;
        this.$parent = null;
        this.$resizers = [];
    }
}
